import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Chip,
  IconButton,
  Button,
  CircularProgress,
  Alert,
  Divider,
  Card,
  CardMedia,
} from '@mui/material';
import {
  ArrowBack,
  Bookmark,
  BookmarkBorder,
  Share,
  Visibility,
  Movie as MovieIcon,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import optimizedNewsApi from '../../services/optimizedNewsApi';
import { getMovieById } from '../../services/mongoApi';
import { useAuth } from '../../contexts/AuthContext';
import { useApp } from '../../contexts/AppContext';
import { showAuthAlert } from '../../utils/authAlerts';
import { getUrl } from 'aws-amplify/storage';

const NewsDetailPage = () => {
  const { newsId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addNotification } = useApp();
  const [news, setNews] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [imageUrl, setImageUrl] = useState(null);
  const [movieDetails, setMovieDetails] = useState(null);

  useEffect(() => {
    loadNews();
  }, [newsId]);

  useEffect(() => {
    if (user) {
      checkBookmark();
    }
  }, [user, newsId]);

  // Track view when news loads and user is available
  useEffect(() => {
    if (news?.id) {
      optimizedNewsApi.trackNewsView(news.id, user?.id);
    }
  }, [news?.id, user?.id]);

  const loadNews = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await optimizedNewsApi.getNewsById(newsId);
      if (result.success && result.item) {
        setNews(result.item);
        
        // Load image URL
        if (result.item.featured_image) {
          try {
            if (result.item.featured_image.startsWith('http')) {
              setImageUrl(result.item.featured_image);
            } else {
              const urlResult = await getUrl({ key: result.item.featured_image, options: { accessLevel: 'guest' } });
              setImageUrl(urlResult.url.toString());
            }
          } catch (imgErr) {
            console.error('Error loading image:', imgErr);
          }
        }
        
        // Load movie details if tmdb_id exists
        if (result.item.tmdb_id) {
          try {
            const movie = await getMovieById(result.item.tmdb_id);
            if (movie) {
              setMovieDetails(movie);
            }
          } catch (movieErr) {
            console.error('Error loading movie details:', movieErr);
          }
        }
      } else {
        setError('News article not found');
      }
    } catch (err) {
      setError('Failed to load news article');
      console.error('Error loading news:', err);
    } finally {
      setLoading(false);
    }
  };

  const checkBookmark = async () => {
    if (!user) return;
    try {
      const result = await optimizedNewsApi.getUserBookmarks(user.id);
      if (result.success) {
        const bookmarked = result.items.some(b => b.newsId === newsId);
        setIsBookmarked(bookmarked);
      }
    } catch (err) {
      console.error('Error checking bookmark:', err);
    }
  };

  const handleBookmark = async () => {
    if (!user) {
      showAuthAlert(addNotification, navigate, 'NEWS_BOOKMARKS');
      return;
    }

    try {
      await optimizedNewsApi.toggleBookmark(newsId);
      setIsBookmarked(!isBookmarked);
    } catch (err) {
      console.error('Error toggling bookmark:', err);
    }
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: news.title,
        text: news.description,
        url: window.location.href,
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
    }
  };

  const extractYouTubeId = (url) => {
    const regex = /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/ ]{11})/;
    const match = url.match(regex);
    return match ? match[1] : null;
  };

  const renderContent = useMemo(() => {
    if (!news || !news.content) return null;
    
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const parts = news.content.split(urlRegex);
    
    return parts.map((part, index) => {
      if (part.match(urlRegex)) {
        const youtubeId = extractYouTubeId(part);
        if (youtubeId) {
          return (
            <Box key={index} sx={{ my: 3 }}>
              <Box
                sx={{
                  position: 'relative',
                  paddingBottom: '56.25%',
                  height: 0,
                  overflow: 'hidden',
                  borderRadius: 2,
                }}
              >
                <iframe
                  src={`https://www.youtube.com/embed/${youtubeId}`}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                  }}
                />
              </Box>
            </Box>
          );
        }
        return (
          <a key={index} href={part} target="_blank" rel="noopener noreferrer" style={{ color: '#1976d2' }}>
            {part}
          </a>
        );
      }
      return <span key={index}>{part}</span>;
    });
  }, [news]);

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error || !news) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">
          {error || 'News article not found'}
          <Button onClick={() => navigate('/news')} sx={{ ml: 2 }}>
            Back to News
          </Button>
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: { xs: 1, sm: 3 }, px: { xs: 1, sm: 3 } }}>
      <Box mb={{ xs: 2, sm: 3 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/news')}
          sx={{ mb: 2, fontSize: { xs: '0.875rem', sm: '1rem' } }}
        >
          Back to News
        </Button>
      </Box>

      <Paper sx={{ p: { xs: 2, sm: 4 } }}>
        {/* Movie Header if associated */}
        {movieDetails && (
          <Card sx={{ mb: 3, bgcolor: 'background.default' }}>
            <Box display="flex" alignItems="center" p={2}>
              {movieDetails.poster ? (
                <CardMedia
                  component="img"
                  image={movieDetails.poster}
                  alt={movieDetails.name}
                  sx={{ 
                    width: 80, 
                    height: 120, 
                    borderRadius: 1, 
                    mr: 2, 
                    objectFit: 'cover',
                    cursor: 'pointer'
                  }}
                  onClick={() => navigate(`/movies/${news.tmdb_id}`)}
                />
              ) : (
                <Box
                  sx={{
                    width: 80,
                    height: 120,
                    borderRadius: 1,
                    mr: 2,
                    bgcolor: 'grey.300',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <MovieIcon sx={{ fontSize: 40, color: 'grey.500' }} />
                </Box>
              )}
              <Box flex={1}>
                <Typography variant="caption" color="text.secondary" gutterBottom>
                  Related Movie
                </Typography>
                <Typography 
                  variant="h6" 
                  fontWeight={700}
                  sx={{ 
                    cursor: 'pointer',
                    '&:hover': { color: 'primary.main' }
                  }}
                  onClick={() => navigate(`/movies/${news.tmdb_id}`)}
                >
                  {movieDetails.name}
                </Typography>
                {movieDetails.release_date && (
                  <Typography variant="body2" color="text.secondary">
                    {new Date(movieDetails.release_date).getFullYear()}
                  </Typography>
                )}
                {movieDetails.genre && (
                  <Typography variant="body2" color="text.secondary">
                    {movieDetails.genre}
                  </Typography>
                )}
              </Box>
            </Box>
          </Card>
        )}

        {imageUrl && (
          <Box mb={{ xs: 2, sm: 3 }}>
            <img
              src={imageUrl}
              alt={news.title}
              style={{
                width: '100%',
                height: 'auto',
                maxHeight: window.innerWidth <= 600 ? '200px' : '400px',
                objectFit: 'cover',
                borderRadius: '8px',
              }}
            />
          </Box>
        )}

        <Box mb={3}>
          <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
            {news.is_breaking && (
              <Chip label="Breaking" color="error" size="small" />
            )}
            {news.is_featured && (
              <Chip label="Featured" color="primary" size="small" />
            )}
            <Chip label={news.category} size="small" variant="outlined" />
          </Box>

          <Typography 
            variant="h3" 
            component="h1" 
            gutterBottom 
            sx={{ 
              fontWeight: 700,
              fontSize: { xs: '1.25rem', sm: '1.75rem', md: '2.125rem' },
              lineHeight: { xs: 1.3, sm: 1.2 }
            }}
          >
            {news.title}
          </Typography>

          {news.description && (
            <Typography 
              variant="h6" 
              color="text.secondary" 
              sx={{ 
                mb: { xs: 2, sm: 3 }, 
                fontWeight: 400,
                fontSize: { xs: '0.875rem', sm: '1rem', md: '1.125rem' },
                lineHeight: 1.5
              }}
            >
              {news.description}
            </Typography>
          )}

          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Box display="flex" alignItems="center" gap={2}>
              <Typography variant="body2" color="text.secondary">
                By {news.author || 'HypeCircle Team'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                •
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {news.published_at ? formatDistanceToNow(new Date(news.published_at), { addSuffix: true }) : 'Recently'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                •
              </Typography>
              <Box display="flex" alignItems="center" gap={0.5}>
                <Visibility sx={{ fontSize: 16 }} />
                <Typography variant="body2" color="text.secondary">
                  {news.view_count || 0} views
                </Typography>
              </Box>
            </Box>

            <Box display="flex" gap={1}>
              <IconButton onClick={handleBookmark}>
                {isBookmarked ? <Bookmark color="primary" /> : <BookmarkBorder />}
              </IconButton>
              <IconButton onClick={handleShare}>
                <Share />
              </IconButton>
            </Box>
          </Box>

          <Divider sx={{ mb: 3 }} />
        </Box>

        <Box 
          sx={{ 
            '& p': { 
              mb: { xs: 1.5, sm: 2 }, 
              fontSize: { xs: '0.875rem', sm: '1rem' },
              lineHeight: { xs: 1.6, sm: 1.7 }
            }, 
            '& img': { 
              maxWidth: '100%', 
              height: 'auto',
              borderRadius: 1,
              my: { xs: 1, sm: 2 }
            },
            '& iframe': { 
              maxWidth: '100%', 
              height: 'auto', 
              aspectRatio: '16/9',
              borderRadius: 1
            },
            '& h1, & h2, & h3, & h4, & h5, & h6': {
              fontSize: { xs: '1rem', sm: '1.125rem' },
              fontWeight: 600,
              mb: { xs: 1, sm: 1.5 },
              mt: { xs: 2, sm: 3 }
            },
            color: 'text.primary'
          }}
        >
          {news.content ? (
            <div dangerouslySetInnerHTML={{ 
              __html: news.content
                .replace(/color:\s*[^;"]+/gi, '')
                .replace(/style=""/gi, '')
            }} />
          ) : (
            <Typography variant="body1" color="text.secondary">
              No content available for this article.
            </Typography>
          )}
        </Box>

        {news.source_url && (
          <Box mt={4} pt={3} borderTop="1px solid" borderColor="divider">
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Source: {news.source || 'External'}
            </Typography>
            <Button
              variant="outlined"
              size="small"
              href={news.source_url}
              target="_blank"
              rel="noopener noreferrer"
            >
              Read Original Article
            </Button>
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default NewsDetailPage;